import React from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import { FaExchangeAlt } from 'react-icons/fa';
import { AirportChange } from '@skygroup/shared/utils/multiCityAirportChange';
import { RootState } from '../../store/store';
import './airportChangeNotice.less';

interface Props {
  change: AirportChange;
  /**
   * Cart sizing — smaller type, no icon padding. The cart already shows the
   * route, pax and price in its own shell, so a notice there has to sit inside
   * a 12px metadata strip rather than read as a banner.
   */
  compact?: boolean;
}

/**
 * "You land at LGW, and your next flight departs from LHR."
 *
 * One component for both places a multi-city itinerary is shown to a customer —
 * the offer card (search results and the view page, which reuses it) and the
 * cart line — so the wording and the tight-transfer treatment cannot drift
 * between the moment of choosing and the moment of paying.
 *
 * The chip itself is only the title: the surrounding card already speaks in
 * airport codes, and a full sentence would force the connector row to wrap.
 * The from/to sentence (and the tight-transfer warning, when it applies) lives
 * on the tooltip so it is still there on hover without occupying a banner.
 *
 * Deliberately code-only, no airport or city names: both flight mappers emit
 * only the IATA code per segment, so a name would cost a per-code lookup, and
 * the surrounding card already speaks entirely in codes.
 */
const AirportChangeNotice = ({ change, compact = false }: Props) => {
  const { searchDatePicker: t } = useSelector(
    (data: RootState) => data.config.translations
  );

  const template = change.withinLeg
    ? t?.airportChangeWithinLeg
    : t?.airportChangeBetweenLegs;

  const body = (template ?? '')
    .replace('{from}', change.arrivalCode)
    .replace('{to}', change.departureCode);

  // Only when the gap is short enough for the transfer to threaten the trip —
  // otherwise a different airport tomorrow is information, not a warning, and
  // dressing it as one trains people to ignore it.
  const tooltip = [
    body,
    change.tight ? t?.airportChangeTightWarning : null,
  ]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(' ');

  const title = t?.airportChangeTitle;
  const accessibleLabel = [title, tooltip]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join('. ');

  const chip = (
    <span
      className={`airport-change-notice${change.tight ? ' is-tight' : ''}${
        compact ? ' is-compact' : ''
      }`}
      data-testid="airport-change-notice"
      aria-label={accessibleLabel || undefined}
    >
      <FaExchangeAlt className="airport-change-notice-icon" />
      {title ? (
        <span className="airport-change-notice-text">{title}</span>
      ) : null}
    </span>
  );

  // A tooltip with no text would leave a hover affordance that reveals nothing.
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};

export default AirportChangeNotice;
